import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import './OverviewTab.css';

function OverviewTab({ config, onTabChange }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchStatus();
  }, [config]);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const response = await api.get('/logic/config/status');
      if (response.data.success) {
        setStatus(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching config status:', err);
      setError('Failed to load configuration status');
    } finally {
      setLoading(false);
    }
  };

  // Map rule IDs to tab names for navigation
  const ruleToTab = {
    eligibleTenants: 'scope',
    receiptQuantityRules: 'quantity',
    amountThresholds: 'thresholds',
    reviewMode: 'review',
    rewards: 'rewards',
    userLimits: 'limits',
    timeRules: 'time'
  };

  // Map rule IDs to icons
  const ruleIcons = {
    eligibleTenants: '🏪',
    receiptQuantityRules: '📝',
    amountThresholds: '💰',
    reviewMode: '✅',
    rewards: '🎁',
    userLimits: '👤',
    timeRules: '📅'
  };

  const getStatusIcon = (checkStatus) => {
    switch (checkStatus) {
      case 'ok':
        return <span className="status-icon ok">✓</span>;
      case 'warning':
        return <span className="status-icon warning">!</span>;
      case 'error':
        return <span className="status-icon error">✗</span>;
      default:
        return <span className="status-icon">?</span>;
    }
  };

  if (loading) {
    return (
      <div className="tab-content overview-tab">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading configuration status...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tab-content overview-tab">
        <div className="error-state">
          <span className="error-icon">⚠️</span>
          <p>{error}</p>
          <button onClick={fetchStatus} className="btn-retry">Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="tab-content overview-tab">
      <div className="tab-header">
        <h2>Configuration Overview</h2>
        <p>Review your campaign configuration status and completeness</p>
      </div>

      {/* Campaign Status Banner */}
      <div className={`campaign-status-banner ${status?.ready ? 'ready' : 'not-ready'}`}>
        <div className="status-content">
          <div className="status-indicator">
            {status?.ready ? (
              <>
                <span className="status-badge ready">✓ READY</span>
                <span className="status-text">Campaign is properly configured and ready to accept submissions</span>
              </>
            ) : (
              <>
                <span className="status-badge not-ready">⚠ NOT READY</span>
                <span className="status-text">
                  {status?.criticalErrors === 1 
                    ? '1 critical configuration is missing' 
                    : `${status?.criticalErrors} critical configurations are missing`}
                </span>
              </>
            )}
          </div>
          <div className="completeness-meter">
            <div className="meter-label">
              <span>Configuration Completeness</span>
              <span className="meter-value">{status?.completeness || 0}%</span>
            </div>
            <div className="meter-bar">
              <div 
                className="meter-fill" 
                style={{ width: `${status?.completeness || 0}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      {/* Configuration Checks Grid */}
      <div className="config-checks-section">
        <h3 className="section-title">Configuration Status</h3>
        <div className="checks-grid">
          {status?.checks?.map((check) => (
            <div 
              key={check.rule} 
              className={`check-card ${check.status} ${check.critical ? 'critical' : ''}`}
              onClick={() => onTabChange(ruleToTab[check.rule])}
            >
              <div className="check-header">
                <span className="check-icon">{ruleIcons[check.rule]}</span>
                <span className="check-name">{check.name}</span>
                {check.critical && (
                  <span className="critical-badge">Required</span>
                )}
                {getStatusIcon(check.status)}
              </div>
              <div className="check-body">
                <p className="check-summary">{check.summary}</p>
                {check.message && (
                  <p className="check-message">{check.message}</p>
                )}
              </div>
              <div className="check-action">
                <span className="action-text">Click to configure →</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Summary */}
      <div className="quick-summary-section">
        <h3 className="section-title">Quick Summary</h3>
        <div className="summary-grid">
          <div className="summary-item">
            <span className="summary-label">Last Updated</span>
            <span className="summary-value">
              {status?.updatedAt 
                ? new Date(status.updatedAt).toLocaleString() 
                : 'Never'}
            </span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Critical Errors</span>
            <span className={`summary-value ${status?.criticalErrors > 0 ? 'error' : 'ok'}`}>
              {status?.criticalErrors || 0}
            </span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Warnings</span>
            <span className={`summary-value ${status?.warnings > 0 ? 'warning' : 'ok'}`}>
              {status?.warnings || 0}
            </span>
          </div>
        </div>
      </div>

      {/* Action Hints */}
      {!status?.ready && (
        <div className="action-hints">
          <h3 className="section-title">Required Actions</h3>
          <ul className="action-list">
            {status?.checks?.filter(c => c.status === 'error').map((check) => (
              <li key={check.rule} className="action-item">
                <span className="action-icon">→</span>
                <span className="action-text">{check.message}</span>
                <button 
                  className="btn-go" 
                  onClick={() => onTabChange(ruleToTab[check.rule])}
                >
                  Configure
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default OverviewTab;
